import React, { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

/**
 * Attendance Trend Chart - Shows attendance patterns over time
 */
const AttendanceTrendChart = ({
  trends,
  totalEmployees,
  loading,
  fromDate,
  toDate,
  onDateRangeChange,
}) => {
  const navigate = useNavigate();
  const [activePeriod, setActivePeriod] = useState('week');
  const [periodOffset, setPeriodOffset] = useState(0);

  const handleViewLateArrivals = () => {
    navigate('/home/reports');
  };

  // Quick period selectors
  const periodOptions = [
    { key: 'day', label: 'Today' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
  ];

  const handlePeriodChange = (period) => {
    setActivePeriod(period);
    setPeriodOffset(0);

    let newFrom, newTo;
    const now = dayjs();

    switch (period) {
      case 'day':
        newFrom = now.format('YYYY-MM-DD');
        newTo = now.format('YYYY-MM-DD');
        break;
      case 'week':
        newFrom = now.startOf('week').format('YYYY-MM-DD');
        newTo = now.endOf('week').format('YYYY-MM-DD');
        break;
      case 'month':
        newFrom = now.startOf('month').format('YYYY-MM-DD');
        newTo = now.endOf('month').format('YYYY-MM-DD');
        break;
      case 'quarter':
        newFrom = now.startOf('quarter').format('YYYY-MM-DD');
        newTo = now.endOf('quarter').format('YYYY-MM-DD');
        break;
      default:
        newFrom = now.startOf('week').format('YYYY-MM-DD');
        newTo = now.endOf('week').format('YYYY-MM-DD');
    }

    onDateRangeChange?.(newFrom, newTo);
  };

  const handlePrevPeriod = () => {
    const newOffset = periodOffset + 1;
    setPeriodOffset(newOffset);

    const now = dayjs();
    let newFrom, newTo;

    switch (activePeriod) {
      case 'day':
        newFrom = now.subtract(newOffset, 'day').format('YYYY-MM-DD');
        newTo = newFrom;
        break;
      case 'week':
        newFrom = now.subtract(newOffset, 'week').startOf('week').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      case 'month':
        newFrom = now.subtract(newOffset, 'month').startOf('month').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case 'quarter':
        newFrom = now.subtract(newOffset * 3, 'month').startOf('quarter').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset * 3, 'month').endOf('quarter').format('YYYY-MM-DD');
        break;
      default:
        newFrom = now.subtract(newOffset, 'week').startOf('week').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'week').endOf('week').format('YYYY-MM-DD');
    }

    onDateRangeChange?.(newFrom, newTo);
  };

  const handleNextPeriod = () => {
    if (periodOffset <= 0) return;
    const newOffset = periodOffset - 1;
    setPeriodOffset(newOffset);

    const now = dayjs();
    let newFrom, newTo;

    switch (activePeriod) {
      case 'day':
        newFrom = now.subtract(newOffset, 'day').format('YYYY-MM-DD');
        newTo = newFrom;
        break;
      case 'week':
        newFrom = now.subtract(newOffset, 'week').startOf('week').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      case 'month':
        newFrom = now.subtract(newOffset, 'month').startOf('month').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case 'quarter':
        newFrom = now.subtract(newOffset * 3, 'month').startOf('quarter').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset * 3, 'month').endOf('quarter').format('YYYY-MM-DD');
        break;
      default:
        newFrom = now.subtract(newOffset, 'week').startOf('week').format('YYYY-MM-DD');
        newTo = now.subtract(newOffset, 'week').endOf('week').format('YYYY-MM-DD');
    }

    onDateRangeChange?.(newFrom, newTo);
  };

  const chartData = useMemo(() => {
    if (!trends || !Array.isArray(trends) || trends.length === 0) return [];

    return trends.map((item) => {
      // Check if this is a future date (is_past_or_today will be false for future dates)
      const isFutureDate = item.is_past_or_today === false;

      return {
        date: activePeriod === 'day'
          ? dayjs(item.date).format('HH:mm')
          : activePeriod === 'month' || activePeriod === 'quarter'
          ? dayjs(item.date).format('D')
          : dayjs(item.date).format('ddd'),
        fullDate: item.date,
        // For future dates, show zero or null values
        Present: isFutureDate ? 0 : (item.present_count || 0),
        Absent: isFutureDate ? 0 : (item.absent_count || 0),
        Late: isFutureDate ? 0 : (item.late_count || 0),
        rate: isFutureDate ? 0 : (item.attendance_rate || 0),
        isFuture: isFutureDate,
      };
    });
  }, [trends, activePeriod]);

  const periodLabel = useMemo(() => {
    if (!fromDate || !toDate) return '';
    const from = dayjs(fromDate);
    const to = dayjs(toDate);

    if (activePeriod === 'day') {
      return from.format('ddd, MMM D, YYYY');
    } else if (activePeriod === 'month') {
      return from.format('MMMM YYYY');
    } else if (activePeriod === 'quarter') {
      return `Q${Math.ceil((from.month() + 1) / 3)} ${from.format('YYYY')}`;
    }
    return `${from.format('MMM D')} - ${to.format('MMM D, YYYY')}`;
  }, [fromDate, toDate, activePeriod]);

  // Summary stats - only count past/today dates
  const stats = useMemo(() => {
    if (!trends || trends.length === 0) return { present: 0, absent: 0, late: 0, avgRate: 0 };

    // Filter to only include past/today dates for summary
    const pastTrends = trends.filter(t => t.is_past_or_today !== false);

    if (pastTrends.length === 0) return { present: 0, absent: 0, late: 0, avgRate: 0 };

    const present = pastTrends.reduce((sum, t) => sum + (t.present_count || 0), 0);
    const absent = pastTrends.reduce((sum, t) => sum + (t.absent_count || 0), 0);
    const late = pastTrends.reduce((sum, t) => sum + (t.late_count || 0), 0);
    const avgRate = pastTrends.reduce((sum, t) => sum + (t.attendance_rate || 0), 0) / pastTrends.length;
    return { present, absent, late, avgRate: avgRate.toFixed(1) };
  }, [trends]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;

      // Show "No data yet" for future dates
      if (data?.isFuture) {
        return (
          <div className="bg-white p-2 shadow-lg rounded border border-gray-200 text-xs">
            <p className="font-medium text-gray-800 mb-1">{data?.fullDate}</p>
            <p className="text-gray-400 italic">No data yet (future date)</p>
          </div>
        );
      }

      return (
        <div className="bg-white p-2 shadow-lg rounded border border-gray-200 text-xs">
          <p className="font-medium text-gray-800 mb-1">{data?.fullDate}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
          <p className="text-gray-1000 mt-1">Rate: {data?.rate}%</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
      {/* Header with Quick Selectors */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-800">Attendance Trends</h3>
          </div>

          {/* Quick Period Selectors */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            {periodOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => handlePeriodChange(opt.key)}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                  activePeriod === opt.key
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrevPeriod}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft size={14} className="text-gray-600" />
            </button>
            <span className="text-xs font-medium text-gray-700 min-w-[140px] text-center">
              {periodLabel}
            </span>
            <button
              onClick={handleNextPeriod}
              disabled={periodOffset <= 0}
              className={`p-1 rounded transition-colors ${
                periodOffset <= 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={14} className="text-gray-600" />
            </button>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-1000"></span>
              {stats.present}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-1000"></span>
              {stats.late}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-1000"></span>
              {stats.absent}
            </span>
            <span className="text-gray-1000">({stats.avgRate}%)</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-36">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Present" stackId="a" fill="#22c55e" radius={[2, 2, 0, 0]} />
              <Bar dataKey="Late" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Absent" stackId="b" fill="#ef4444" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            No attendance data for this period
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-2 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm bg-green-1000"></span>
          Present
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm bg-yellow-1000"></span>
          Late
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm bg-red-1000"></span>
          Absent
        </span>
      </div>
    </div>
  );
};

export default AttendanceTrendChart;
